import { revokeCurrentStaffSession } from '../../auth/staffSession';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import logo from '../../../assets/images/farmstream_logo.png';
import { FullScreenSpinner } from '../../../components/Spinner/Spinner';
import { useAppDispatch } from '../../../store';
import { authSelector, clearToken } from '../../auth/state/authSlice';
import { formatNumberToCurrency, formatDateToText, getErrorMessage } from '../../../utils';
import {
  useTradeMeQuery,
  useTradeCustomersQuery,
  useTradeAddCustomerMutation,
  useTradePayoutQuery,
  useTradeSavePayoutMutation,
  TradeCustomer
} from '../api/tradeApi';
import { TradeRevenueChart } from '../components/TradeRevenueChart';

const money = (pence: number, currency?: string) =>
  formatNumberToCurrency((pence || 0) / 100, (currency || 'gbp').toUpperCase());

const onlyDigits = (value: string) => value.replace(/\D/g, '');

// Format up to 6 digits as a UK sort code: 12-34-56
const formatSortCode = (value: string) => {
  const digits = onlyDigits(value).slice(0, 6);
  return digits.replace(/(\d{2})(?=\d)/g, '$1-');
};

export const TradeDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token } = useSelector(authSelector);

  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [accountName, setAccountName] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/trade', { replace: true });
    }
  }, [token, navigate]);

  const { data: profile, isLoading: profileLoading, error: profileError } = useTradeMeQuery(undefined, {
    skip: !token
  });
  const {
    data: customersData,
    isLoading: customersLoading,
    isFetching: customersFetching,
    error: customersError,
    refetch
  } = useTradeCustomersQuery(undefined, { skip: !token });
  const { data: payout, isLoading: payoutLoading } = useTradePayoutQuery(undefined, { skip: !token });
  const [addCustomer, { isLoading: addingCustomer }] = useTradeAddCustomerMutation();
  const [savePayout, { isLoading: savingPayout }] = useTradeSavePayoutMutation();

  const handleLogout = async () => {
    await revokeCurrentStaffSession();
    dispatch(clearToken());
    navigate('/trade', { replace: true });
  };

  const handleCopy = async () => {
    if (!profile?.referral_link) return;
    try {
      await navigator.clipboard.writeText(profile.referral_link);
      toast.success('Referral link copied');
    } catch {
      toast.error('Could not copy link — please copy it manually');
    }
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newCustomerEmail.trim();
    if (!email) return;
    try {
      const res = await addCustomer({ email }).unwrap();
      toast.success(res.message || 'Customer added');
      setNewCustomerEmail('');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not add customer'));
    }
  };

  const handleSavePayout = async (e: React.FormEvent) => {
    e.preventDefault();
    const sortDigits = onlyDigits(sortCode);
    const accountDigits = onlyDigits(accountNumber);
    if (!accountName.trim() || !sortDigits || !accountDigits) {
      toast.error('Please fill in all payout fields');
      return;
    }
    if (sortDigits.length !== 6) {
      toast.error('Sort code must be 6 digits');
      return;
    }
    if (accountDigits.length !== 8) {
      toast.error('Account number must be 8 digits');
      return;
    }
    try {
      const res = await savePayout({
        account_name: accountName.trim(),
        sort_code: sortDigits,
        account_number: accountDigits
      }).unwrap();
      toast.success(res.message || 'Payout details saved');
      setAccountName('');
      setSortCode('');
      setAccountNumber('');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save payout details'));
    }
  };

  if (!token) return null;

  if (profileLoading || customersLoading) {
    return <FullScreenSpinner />;
  }

  if (profileError || customersError) {
    const message = getErrorMessage(
      profileError || customersError,
      'We could not load your trade portal. Please sign in again.'
    );
    return (
      <ErrorWrap>
        <ErrorCard>
          <h3>Unable to load your portal</h3>
          <p>{message}</p>
          <PrimaryButton onClick={handleLogout}>Back to sign in</PrimaryButton>
        </ErrorCard>
      </ErrorWrap>
    );
  }

  const ratePct = Math.round((profile?.commission_rate ?? 0.2) * 100);
  const summary = customersData?.summary;
  const customers = customersData?.customers ?? [];

  return (
    <Page>
      <TopBar>
        <Brand>
          <Logo src={logo} alt="Farmstream logo" />
          <BrandText>
            <strong>Trade Partner Portal</strong>
            <span>{profile?.business_name}</span>
          </BrandText>
        </Brand>
        <GhostButton onClick={handleLogout}>Log out</GhostButton>
      </TopBar>

      <Content>
        <HeroCard>
          <HeroTitle>Thanks for partnering with Farmstream{profile?.business_name ? `, ${profile.business_name}` : ''} 🤝</HeroTitle>
          <HeroText>
            Our best customers come from trusted trade recommendations — so we share the rewards with
            you. Every customer you send our way earns you{' '}
            <HeroHighlight>{ratePct}% of everything they pay</HeroHighlight> — not just on their first
            SIM, but on every SIM and every renewal on their account, for as long as they stay with us.
          </HeroText>
          <HeroText>
            No caps, no expiry, no small print. The more people you help get connected, the more you
            earn — and you can track it all right here, in real time.
          </HeroText>
        </HeroCard>

        <ReferralCard>
          <CardTitle>Your referral link</CardTitle>
          <CardHint>
            Share this link with your customers. Anyone who activates a SIM through it — and any
            future SIMs on their email — earns you <strong>{ratePct}%</strong> of what they pay.
          </CardHint>
          <ReferralRow>
            <ReferralInput value={profile?.referral_link ?? ''} readOnly onFocus={(e) => e.target.select()} />
            <PrimaryButton onClick={handleCopy}>Copy link</PrimaryButton>
          </ReferralRow>
          <ReferralCode>
            Referral code: <code>{profile?.referral_code}</code>
          </ReferralCode>
        </ReferralCard>

        <AddCustomerCard>
          <CardTitle>Add a customer</CardTitle>
          <CardHint>
            Prefer not to share the link? Register a customer's email directly — either method works.
            They'll appear below as <strong>pending</strong> until they activate, then commission is
            tracked automatically.
          </CardHint>
          <form onSubmit={handleAddCustomer}>
            <ReferralRow>
              <ReferralInput
                as="input"
                type="email"
                placeholder="customer@example.com"
                value={newCustomerEmail}
                onChange={(e) => setNewCustomerEmail(e.target.value)}
              />
              <PrimaryButton type="submit" disabled={addingCustomer}>
                {addingCustomer ? 'Adding…' : 'Add customer'}
              </PrimaryButton>
            </ReferralRow>
          </form>
        </AddCustomerCard>

        <StatsGrid>
          <StatCard>
            <StatValue>{summary?.total_customers ?? 0}</StatValue>
            <StatLabel>Referred customers</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{summary?.activated_customers ?? 0}</StatValue>
            <StatLabel>Activated</StatLabel>
          </StatCard>
          <StatCard $highlight>
            <StatValue>{money(summary?.total_commission ?? 0, customersData?.currency)}</StatValue>
            <StatLabel>Your commission ({ratePct}%)</StatLabel>
          </StatCard>
        </StatsGrid>

        <ChartCard>
          <ChartHeader>
            <CardTitle>Commission over time</CardTitle>
            <ChartSubtitle>Monthly commission earned</ChartSubtitle>
          </ChartHeader>
          <TradeRevenueChart
            data={customersData?.monthly_revenue ?? []}
            currency={customersData?.currency ?? 'gbp'}
          />
        </ChartCard>

        <TableCard>
          <TableHeader>
            <CardTitle>Your customers</CardTitle>
            <GhostButton onClick={() => refetch()} disabled={customersFetching}>
              {customersFetching ? 'Refreshing…' : 'Refresh'}
            </GhostButton>
          </TableHeader>

          {customers.length === 0 ? (
            <EmptyState>
              No referrals yet. Share your link above — customers will appear here once they activate.
            </EmptyState>
          ) : (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <Th>Customer</Th>
                    <Th>Status</Th>
                    <Th>SIMs</Th>
                    <Th>Referred</Th>
                    <Th align="right">Your commission</Th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c: TradeCustomer) => (
                    <tr key={c.email}>
                      <Td>{c.email}</Td>
                      <Td>
                        <Badge $activated={c.status === 'activated'}>
                          {c.status === 'activated' ? 'Activated' : 'Pending'}
                        </Badge>
                      </Td>
                      <Td title={c.sims.join(', ')}>{c.sims.length || '—'}</Td>
                      <Td>{c.referred_at ? formatDateToText(c.referred_at) : '—'}</Td>
                      <Td align="right">
                        <Commission>{money(c.commission, c.currency)}</Commission>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
        </TableCard>

        <PayoutCard>
          <CardTitle>Payout details</CardTitle>
          <PayoutNotice>
            Payouts are processed manually for now — email{' '}
            <a href="mailto:support@farmstream.co.uk">support@farmstream.co.uk</a> to request a
            payout. We keep these details securely on file so we're ready when you do.
          </PayoutNotice>
          {!payoutLoading && payout?.has_details && (
            <OnFileRow>
              On file: <strong>{payout.account_name}</strong> · sort code{' '}
              <strong>{payout.sort_code}</strong> · account{' '}
              <strong>{payout.account_number_masked}</strong>
              {payout.updated_at ? ` · updated ${formatDateToText(payout.updated_at)}` : ''}
            </OnFileRow>
          )}
          <form onSubmit={handleSavePayout}>
            <PayoutGrid>
              <Field>
                <FieldLabel>Account holder name</FieldLabel>
                <FieldInput
                  type="text"
                  placeholder="J. Smith"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel>Sort code</FieldLabel>
                <FieldInput
                  type="text"
                  inputMode="numeric"
                  placeholder="12-34-56"
                  value={sortCode}
                  onChange={(e) => setSortCode(formatSortCode(e.target.value))}
                  maxLength={8}
                />
              </Field>
              <Field>
                <FieldLabel>Account number</FieldLabel>
                <FieldInput
                  type="text"
                  inputMode="numeric"
                  placeholder="12345678"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(onlyDigits(e.target.value).slice(0, 8))}
                  maxLength={8}
                />
              </Field>
            </PayoutGrid>
            <PayoutActions>
              <PrimaryButton type="submit" disabled={savingPayout}>
                {savingPayout ? 'Saving…' : payout?.has_details ? 'Update details' : 'Save details'}
              </PrimaryButton>
            </PayoutActions>
          </form>
        </PayoutCard>
      </Content>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100dvh;
  background-color: ${({ theme }) => theme.colors.gray};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background-color: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray500};
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Logo = styled.img`
  width: 3rem;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  strong {
    color: ${({ theme }) => theme.colors.gray900};
  }
  span {
    font-size: 0.85rem;
    color: #667085;
  }
`;

const Content = styled.div`
  max-width: 60rem;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  padding: 1.25rem;
`;

const HeroCard = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, #1f86ab 100%);
  color: white;
  border-radius: 0.75rem;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 6px 18px rgba(41, 168, 211, 0.25);
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 1.4rem;

  @media (max-width: 500px) {
    font-size: 1.2rem;
  }
`;

const HeroText = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.5;
  opacity: 0.95;
`;

const HeroHighlight = styled.strong`
  background-color: rgba(255, 255, 255, 0.2);
  padding: 0.05rem 0.4rem;
  border-radius: 0.3rem;
  white-space: nowrap;
`;

const ReferralCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AddCustomerCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const ChartHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const ChartSubtitle = styled.span`
  font-size: 0.85rem;
  color: #667085;
`;

const PayoutCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const PayoutNotice = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #667085;

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const OnFileRow = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.gray900};
  background-color: ${({ theme }) => theme.colors.gray};
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
`;

const PayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr;
  gap: 0.75rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const FieldLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray900};
`;

const FieldInput = styled.input`
  padding: 0.55rem 0.6rem;
  border-radius: 0.4rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  font-size: 0.95rem;
`;

const PayoutActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.85rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray900};
`;

const CardHint = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #667085;
`;

const ReferralRow = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const ReferralInput = styled.input`
  flex: 1;
  padding: 0.6rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  background-color: ${({ theme }) => theme.colors.gray};
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.gray900};
`;

const ReferralCode = styled.div`
  font-size: 0.85rem;
  color: #667085;

  code {
    background-color: ${({ theme }) => theme.colors.gray};
    padding: 0.1rem 0.4rem;
    border-radius: 0.3rem;
    color: ${({ theme }) => theme.colors.gray900};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 700px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)<{ $highlight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  ${({ $highlight, theme }) =>
    $highlight &&
    `
    background-color: ${theme.colors.primary100};
    border-color: ${theme.colors.primary300};
  `}
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #667085;
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray500};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const Th = styled.th<{ align?: 'right' | 'left' }>`
  text-align: ${({ align }) => align ?? 'left'};
  padding: 0.75rem 1.25rem;
  color: #667085;
  font-weight: 600;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray500};
  white-space: nowrap;
`;

const Td = styled.td<{ align?: 'right' | 'left' }>`
  text-align: ${({ align }) => align ?? 'left'};
  padding: 0.75rem 1.25rem;
  color: ${({ theme }) => theme.colors.gray900};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray};
  white-space: nowrap;
`;

const Badge = styled.span<{ $activated?: boolean }>`
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: white;
  background-color: ${({ $activated, theme }) =>
    $activated ? theme.colors.green900 : theme.colors.yellow900};
`;

const Commission = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.green900};
`;

const EmptyState = styled.div`
  padding: 2.5rem 1.25rem;
  text-align: center;
  color: #667085;
`;

const PrimaryButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.6rem 1.25rem;
  font-size: 0.95rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.gray};
`;

const ErrorCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  padding: 2rem;
  max-width: 420px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.gray900};
  }
  p {
    margin: 0;
    color: #667085;
  }
`;
